import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. The link may be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full text-center">
          <p className="text-red-400 text-sm mb-4">Invalid reset link. No token provided.</p>
          <button
            onClick={() => navigate("/admin")}
            className="text-amber-400 hover:text-amber-300 text-sm transition-colors"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            Paperwork<span className="text-amber-400">Depot</span>
          </h1>
          <p className="text-zinc-500 mt-2 text-sm tracking-wide uppercase">Reset Password</p>
        </div>

        {success ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center space-y-4" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-green-900/50 border border-green-700 flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-white text-lg font-bold">Password Reset Complete</h2>
            <p className="text-zinc-400 text-sm">Your password has been updated. You can now sign in.</p>
            <button
              onClick={() => navigate("/admin")}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 px-8 rounded-lg transition-colors text-sm"
              data-testid="go-to-login-btn"
            >
              Sign In
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleReset}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 space-y-5"
            data-testid="reset-password-form"
          >
            <p className="text-zinc-400 text-sm">Enter your new password below.</p>

            {error && (
              <div className="bg-red-950 border border-red-800 text-red-300 text-sm px-4 py-3 rounded-lg" data-testid="reset-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-zinc-400 text-sm mb-1.5">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="At least 8 characters"
                minLength={8}
                required
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-zinc-400 text-sm mb-1.5">Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Re-enter password"
                minLength={8}
                required
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
              data-testid="reset-submit-button"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/admin")}
                className="text-zinc-500 hover:text-amber-400 text-sm transition-colors"
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
